/**
 * تجريد تخزين الملفات (Blob storage).
 *
 * الوضع المحلي يستخدم IndexedDB، ووضع Supabase يستخدم Storage bucket خاص.
 * الواجهة موحّدة حتى لا يعرف منطق المرفقات أين تُخزَّن الملفات فعليًا.
 * الروابط دائمًا مؤقتة: لا يوجد رابط عام دائم لأي مرفق.
 */
export interface BlobStore {
  put(path: string, bytes: ArrayBuffer, mimeType: string): Promise<void>;
  get(path: string): Promise<ArrayBuffer | null>;
  remove(path: string): Promise<void>;
  /** رابط مؤقت للعرض فقط، ينتهي بعد المدة المحددة */
  createUrl(path: string, expiresInSeconds: number): Promise<string>;
  exists(path: string): Promise<boolean>;
}

export function buildStoragePath(input: {
  companyId: string;
  taskId: string | null;
  attachmentId: string;
  fileName: string;
}): string {
  const safeName = sanitizeFileName(input.fileName);
  const folder = input.taskId ? `tasks/${input.taskId}` : 'general';
  return `${input.companyId}/${folder}/${input.attachmentId}-${safeName}`;
}

/**
 * أسماء مفاتيح التخزين يجب أن تكون ASCII فقط: Supabase Storage يرفض المفاتيح
 * غير اللاتينية بخطأ InvalidKey (400). الاسم الأصلي يبقى في قاعدة البيانات
 * للعرض، بينما المسار يستخدم اسمًا آمنًا.
 */
export function sanitizeFileName(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  const rawExtension = dot > 0 ? fileName.slice(dot + 1) : '';
  const extension = /^[A-Za-z0-9]{1,10}$/.test(rawExtension)
    ? rawExtension.toLowerCase()
    : '';
  const stem = (dot > 0 ? fileName.slice(0, dot) : fileName)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
  return `${stem || 'file'}${extension ? `.${extension}` : ''}`;
}

export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
